import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import SplitTitle from "../../../common/SplitTitle";
import { useMutation } from "@tanstack/react-query";
import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { errorLogger } from "@/Common/helpers/logger";
import TimePicker from "./TimePicker";
import { ICallActivityPerformance } from "@/Settings/types/Common/model";
import { CSSProperties, ChangeEvent, useEffect, useState } from "react";
import { TPickerName } from "@/Settings/types/SystemSettings/callActivity";

const gridItemStyle = {
	paddingRight: "60px",
};

type TCallActivityPerformanceProps = {
	locationId: string;
	callActivityPerformance?: ICallActivityPerformance;
};

const CallActivityPerformance = (props: TCallActivityPerformanceProps) => {
	const { locationId, callActivityPerformance } = props;
	const [timers, setTimers] = useState<ICallActivityPerformance>();

	const { mutate, isPending } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: (input: ICallActivityPerformance) =>
			updateSystemSettings(locationId, { callActivityPerformance: input }, "CALL_ACTIVITY_PERFORMANCE"),
		onError: (error) => errorLogger(error.message),
	});

	const updateData = (pickerName: TPickerName, timeType: string, id: number) => {
		const updatedTimers = { ...(timers ?? {}), [pickerName]: { ...timers?.[pickerName], [timeType]: id } };
		setTimers(updatedTimers as ICallActivityPerformance);
		mutate(updatedTimers as ICallActivityPerformance);
	};

	const handleCheckChange = (e: ChangeEvent<HTMLInputElement>, key: string) => {
		const value = e.target.checked;
		const updatedTimers = { ...(timers ?? {}), [key]: value };
		setTimers(updatedTimers as ICallActivityPerformance);
		mutate(updatedTimers as ICallActivityPerformance);
	};

	useEffect(() => {
		if (callActivityPerformance) {
			setTimers(callActivityPerformance);
		}
	}, [callActivityPerformance]);

	return (
		<Grid>
			<Grid container className="flex flex-row items-end mt-[16px]">
				<TimePickerModule
					hideCheckbox
					callActivityPerformance={timers}
					isPending={isPending}
					handleCheckChange={handleCheckChange}
					timePickerKey="maxAllowableTurnaroundTime"
					updateData={updateData}
					title="Max Allowable"
					subTitle="Turnaround Time"
					includeHours
					includeMinutes
					minutesInterval={15}
					hoursMax={169}
				/>
				<TimePickerModule
					callActivityPerformance={timers}
					isPending={isPending}
					handleCheckChange={handleCheckChange}
					checkKey="enableTalkTime"
					timePickerKey="minimumTalkDuration"
					updateData={updateData}
					sx={{ marginLeft: "-7px", marginBottom: "6px" }}
					title="Minimum"
					subTitle="Talk Duration"
					includeMinutes
					includeSeconds
					minutesMax={6}
					secondsInterval={2}
				/>
				<TimePickerModule
					callActivityPerformance={timers}
					isPending={isPending}
					handleCheckChange={handleCheckChange}
					checkKey="enableRingTime"
					timePickerKey="minimumRingDuration"
					updateData={updateData}
					sx={{ marginLeft: "-7px", marginBottom: "6px" }}
					title="Minimum"
					subTitle="Ring Duration"
					includeSeconds
				/>
				<TimePickerModule
					callActivityPerformance={timers}
					isPending={isPending}
					handleCheckChange={handleCheckChange}
					checkKey="enableTotalTime"
					timePickerKey="minimumTotalDuration"
					updateData={updateData}
					sx={{ marginLeft: "-7px", marginBottom: "6px" }}
					title="Minimum"
					subTitle="Total Duration"
					includeMinutes
					includeSeconds
					minutesMax={6}
					secondsInterval={2}
				/>
			</Grid>
		</Grid>
	);
};

type TTimePickerModuleProps = {
	callActivityPerformance?: ICallActivityPerformance;
	isPending: boolean;
	handleCheckChange: (e: ChangeEvent<HTMLInputElement>, key: string) => void;
	checkKey?: "enableTalkTime" | "enableRingTime" | "enableTotalTime";
	timePickerKey: TPickerName;
	updateData: (pickerName: TPickerName, timeType: string, id: number) => void;
	sx?: CSSProperties;
	title: string;
	subTitle: string;
	hideCheckbox?: boolean;
	hoursMax?: number;
	hoursInterval?: number;
	minutesMax?: number;
	minutesInterval?: number;
	secondsMax?: number;
	secondsInterval?: number;
	includeHours?: boolean;
	includeMinutes?: boolean;
	includeSeconds?: boolean;
};

const TimePickerModule = (props: TTimePickerModuleProps) => {
	const {
		callActivityPerformance,
		isPending,
		handleCheckChange,
		updateData,
		checkKey = "",
		timePickerKey,
		sx,
		title,
		subTitle,
		hideCheckbox,
		includeHours,
		includeMinutes,
		includeSeconds,
		hoursMax,
		hoursInterval,
		minutesMax,
		minutesInterval,
		secondsMax,
		secondsInterval,
	} = props;
	return (
		<Grid item xs={3} sx={gridItemStyle}>
			{!hideCheckbox && (
				<FormControlLabel
					sx={sx}
					control={
						<Checkbox
							checked={!!checkKey && !!callActivityPerformance?.[checkKey]}
							onChange={(e) => {
								handleCheckChange(e, checkKey);
							}}
						/>
					}
					label="Enabled"
					labelPlacement="end"
					className="payroll-submission-position"
					disabled={isPending}
				/>
			)}
			<SplitTitle title={title} subTitle={subTitle} className="!mb-[12px]" />
			<TimePicker
				includeHours={includeHours}
				includeMinutes={includeMinutes}
				includeSeconds={includeSeconds}
				hoursMax={hoursMax}
				hoursInterval={hoursInterval}
				minutesMax={minutesMax}
				minutesInterval={minutesInterval}
				secondsMax={secondsMax}
				secondsInterval={secondsInterval}
				initialValue={callActivityPerformance?.[timePickerKey]}
				readOnly={isPending || (!!checkKey && !callActivityPerformance?.[checkKey])}
				updateData={updateData}
				name={timePickerKey}
			/>
		</Grid>
	);
};

export default CallActivityPerformance;
